/*
 * The only place colours, spacing, type and easings are defined.
 *
 * The look is the one a Tesla owner already lives in: near-black surfaces, white type, one
 * hairline between things, pill buttons, generous space, and colour rationed to the moments
 * that matter — the cars, and the wave. Every usual trick for "premium" is banned by the
 * MCU 2 budget: no filter, no backdrop-filter, no blur, no animated box-shadow. Depth comes
 * from pre-baked pixels (sprite glows, a tiled noise texture, a painted vignette) and motion
 * is composited: transform and opacity only. See docs/decisions/0014-design-without-blur.md.
 */
:root {
  /* Ground: near-black, neutral, never pure #000, which smears on the car screen. */
  --bg-0: #08090b;
  --bg-1: #0f1013;
  --bg-2: #17191d;
  --bg-3: #1f2227;
  --bg-sheet: rgba(15, 16, 19, 0.96);
  --line: #24272d;
  --line-soft: #1a1c21;
  --line-strong: #363a42;

  --fg: #f5f6f7;
  --fg-dim: #9da2aa;
  --fg-faint: #62676f;

  /* Paint: the primary action is white on black, like every big button in the car. */
  --paint: #ffffff;
  --paint-fg: #0a0a0b;

  /* One accent: this is you, and this is a wave. Live state and nothing else. */
  --accent: #6ee7ff;
  --accent-deep: #1d7f96;
  --accent-soft: rgba(110, 231, 255, 0.14);
  /* Rationed to the wave moment only. */
  --accent-warm: #ffd08a;
  --ok: #7ee0a8;
  --danger: #ff8b8b;

  /*
   * One layering scale. Ad-hoc z-indexes are how a dialog ends up rendering behind the
   * screen that opened it, which is exactly what happened before this existed.
   */
  --z-map: 1;
  --z-overlay: 2;
  --z-vignette: 3;
  --z-attribution: 4;
  /* The one-shot edge flash of a received wave: over the map, under every control. */
  --z-flash: 5;
  --z-hud: 10;
  --z-wave: 20;
  --z-screen: 30;
  --z-scrim: 40;
  --z-sheet: 50;
  --z-milestone: 60;
  --z-toast: 70;

  --r-sm: 10px;
  --r-md: 16px;
  --r-lg: 24px;
  --r-xl: 32px;
  --r-full: 999px;

  --sp-1: 4px;
  --sp-2: 8px;
  --sp-3: 12px;
  --sp-4: 16px;
  --sp-5: 24px;
  --sp-6: 32px;
  --sp-7: 48px;
  --sp-8: 64px;

  /* Nothing interactive comes closer than this to a screen edge (brief 6). */
  --edge: 40px;
  /* Minimum touch target on a car screen. */
  --touch: 64px;

  /* Pre-baked texture: what stands in for frosted glass, since blur is banned. */
  --noise: url('/noise.png');

  --font:
    system-ui, -apple-system, 'SF Pro Text', 'Segoe UI', Roboto, 'Helvetica Neue', Helvetica,
    Arial, sans-serif;
  /* Numerals are the same face as everything else, tabular: no dotted zeros, no jitter. */
  --font-num: var(--font);

  --t-micro: 600 11px/1.2 var(--font);
  --t-hud: 500 13px/1.25 var(--font);
  --t-body: 450 16px/1.45 var(--font);
  --t-strong: 600 16px/1.3 var(--font);
  --t-title: 600 24px/1.2 var(--font);
  --t-hero: 600 40px/1.05 var(--font);
  --t-display: 600 56px/1 var(--font);

  /* Motion is the personality. Everything entering settles; one thing overshoots. */
  --e-out: cubic-bezier(0.16, 1, 0.3, 1);
  --e-inout: cubic-bezier(0.65, 0, 0.35, 1);
  --e-spring: cubic-bezier(0.34, 1.56, 0.64, 1);
  --d-fast: 120ms;
  --d-base: 220ms;
  --d-slow: 420ms;
  --d-story: 900ms;
}

* {
  box-sizing: border-box;
  -webkit-tap-highlight-color: transparent;
}

html,
body,
#root {
  /*
   * dvh, not %: on iOS Safari a percentage height resolves against the large viewport, so
   * the app ran behind the URL bar at the top and left a black band above the toolbar at the
   * bottom. dvh tracks the bar as it collapses. vh stays as the fallback for older engines.
   */
  height: 100vh;
  height: 100dvh;
  margin: 0;
  overflow: hidden;
  overscroll-behavior: none;
  position: fixed;
  inset: 0;
}

body {
  background: var(--bg-0);
  color: var(--fg);
  font: var(--t-body);
  text-rendering: optimizeLegibility;
  -webkit-font-smoothing: antialiased;
  touch-action: manipulation;
}

button {
  font: inherit;
  color: inherit;
  border: 0;
  background: none;
  cursor: pointer;
}

input {
  font: inherit;
  color: inherit;
}

h1,
h2,
h3,
p {
  margin: 0;
}

/* Counters must never reflow as digits change. */
.num {
  font-family: var(--font-num);
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;
}

/* Small tracked capitals: the label above a group, the caption under a number. */
.eyebrow {
  font: var(--t-micro);
  color: var(--fg-faint);
  text-transform: uppercase;
  letter-spacing: 0.12em;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
}

/* A driver who asked for less motion gets less motion, everywhere. */
@media (prefers-reduced-motion: reduce) {
  *,
  *::before,
  *::after {
    animation-duration: 1ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 1ms !important;
  }
}
